import { c as _c } from "react/compiler-runtime";
import { PopularBadge } from "@/components/ui/popular-badge";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { TelehealthBadge } from "@/components/ui/telehealth-badge";
import { useCatalogImageLayout } from "@/contexts/catalog-image-layout-provider";
import { cx } from "@/lib/tailwind-utils";
import { PublicServiceDetail } from "@/types/service";
import { AddonsAvailableBadge, DisabledBadge, InfoButton, PromotionBadge, ServiceDescription, ServiceTitle } from "./components";
import { BookingFeeNote, PriceDisplay, PricingInfo } from "./price-display";
import { SelectionCircle } from "./selection-circle";
interface ContentProps {
  service: PublicServiceDetail;
  isSelected: boolean;
  selectionCount?: number;
  maxQuantity?: number;
  disabled: boolean;
  onQuantityChange?: (serviceId: string, quantity: number) => void;
  showBookingFee: boolean;
  bookingFee: number;
  pricing: PricingInfo;
  onInfoClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hasAddons?: boolean;
  durationMin?: number;
  durationMax?: number;
  expanded?: boolean;
  collapsibleDescription?: boolean;
  standalone?: boolean;
  showDuration?: boolean;
  pricePrefix?: string;
}
const Content = t0 => {
  const $ = _c(78);
  const {
    service,
    isSelected,
    selectionCount: t1,
    maxQuantity,
    disabled,
    onQuantityChange,
    showBookingFee,
    bookingFee,
    pricing,
    onInfoClick,
    hasAddons,
    durationMin,
    durationMax,
    expanded,
    collapsibleDescription: t2,
    standalone: t3,
    showDuration: t4,
    pricePrefix
  } = t0;
  const selectionCount = t1 === undefined ? 0 : t1;
  const collapsibleDescription = t2 === undefined ? false : t2;
  const standalone = t3 === undefined ? false : t3;
  const showDuration = t4 === undefined ? true : t4;
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const usesSquareImage = catalogImageLayout === "SQUARE";
  let t5;
  if ($[0] !== disabled || $[1] !== service.name) {
    t5 = <ServiceTitle name={service.name} disabled={disabled} className="line-clamp-3" />;
    $[0] = disabled;
    $[1] = service.name;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== service.isPopular) {
    t6 = service.isPopular && <PopularBadge />;
    $[3] = service.isPopular;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== service.promotionName) {
    t7 = service.promotionName && <PromotionBadge promotionName={service.promotionName} />;
    $[5] = service.promotionName;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t6 || $[9] !== t7) {
    t8 = <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1.5">{t5}{t6}{t7}</div>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const titleBlock = t8;
  const t9 = durationMin ?? service.duration;
  let t10;
  if ($[11] !== bookingFee || $[12] !== durationMax || $[13] !== pricePrefix || $[14] !== pricing || $[15] !== service.bookingFeeAutoToCredit || $[16] !== showBookingFee || $[17] !== showDuration || $[18] !== t9) {
    t10 = <PriceDisplay pricing={pricing} bookingFee={bookingFee} showBookingFee={showBookingFee} bookingFeeAutoToCredit={service.bookingFeeAutoToCredit} duration={t9} durationMax={durationMax} showDuration={showDuration} prefix={pricePrefix} variant="desktop" />;
    $[11] = bookingFee;
    $[12] = durationMax;
    $[13] = pricePrefix;
    $[14] = pricing;
    $[15] = service.bookingFeeAutoToCredit;
    $[16] = showBookingFee;
    $[17] = showDuration;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const priceDisplay = t10;
  let t11;
  if ($[20] !== isSelected || $[21] !== maxQuantity || $[22] !== onQuantityChange || $[23] !== selectionCount || $[24] !== service.allowMultipleBookings || $[25] !== service.id) {
    t11 = isSelected && service.allowMultipleBookings && onQuantityChange ? <div onClick={_temp}><QuantityStepper value={selectionCount} min={0} max={maxQuantity} onChange={quantity => onQuantityChange(service.id, quantity)} className="shadow-sm" /></div> : <SelectionCircle isSelected={isSelected} count={selectionCount} />;
    $[20] = isSelected;
    $[21] = maxQuantity;
    $[22] = onQuantityChange;
    $[23] = selectionCount;
    $[24] = service.allowMultipleBookings;
    $[25] = service.id;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const selectionControl = t11;
  let t12;
  if ($[27] !== priceDisplay || $[28] !== selectionControl) {
    t12 = <div className="flex flex-shrink-0 items-start gap-2">{priceDisplay}{selectionControl}</div>;
    $[27] = priceDisplay;
    $[28] = selectionControl;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== titleBlock) {
    t13 = <div className="flex items-start justify-between gap-3">{titleBlock}{t12}</div>;
    $[30] = t12;
    $[31] = titleBlock;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== collapsibleDescription || $[34] !== expanded || $[35] !== service.description || $[36] !== standalone) {
    t14 = service.description && <ServiceDescription description={service.description} standalone={standalone} collapsible={collapsibleDescription} className={cx("mt-2 max-w-2xl", expanded && !collapsibleDescription && "line-clamp-none")} />;
    $[33] = collapsibleDescription;
    $[34] = expanded;
    $[35] = service.description;
    $[36] = standalone;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== bookingFee || $[39] !== service.bookingFeeAutoToCredit || $[40] !== service.displayPrice || $[41] !== showBookingFee) {
    t15 = <BookingFeeNote showBookingFee={showBookingFee} displayPrice={service.displayPrice} bookingFee={bookingFee} bookingFeeAutoToCredit={service.bookingFeeAutoToCredit} variant="desktop" />;
    $[38] = bookingFee;
    $[39] = service.bookingFeeAutoToCredit;
    $[40] = service.displayPrice;
    $[41] = showBookingFee;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== service.isVirtual) {
    t16 = service.isVirtual && <TelehealthBadge />;
    $[43] = service.isVirtual;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== hasAddons) {
    t17 = hasAddons && <AddonsAvailableBadge />;
    $[45] = hasAddons;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== service.isEventPrice || $[48] !== service.promotionName) {
    t18 = service.promotionName && !service.isEventPrice && <span className="text-caption font-medium text-status-success-text">Promotion applied</span>;
    $[47] = service.isEventPrice;
    $[48] = service.promotionName;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== disabled || $[51] !== expanded) {
    t19 = !expanded && disabled && <DisabledBadge />;
    $[50] = disabled;
    $[51] = expanded;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t15 || $[54] !== t16 || $[55] !== t17 || $[56] !== t18 || $[57] !== t19) {
    t20 = <div className="flex items-center gap-3">{t15}{t16}{t17}{t18}{t19}</div>;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== expanded || $[60] !== onInfoClick) {
    t21 = expanded && onInfoClick && <InfoButton onClick={onInfoClick} showTooltip={true} />;
    $[59] = expanded;
    $[60] = onInfoClick;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t20 || $[63] !== t21) {
    t22 = <div className="mt-2 flex items-center justify-between">{t20}{t21}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== t13 || $[66] !== t14 || $[67] !== t22) {
    t23 = <div className="min-w-0 flex-1">{t13}{t14}{t22}</div>;
    $[65] = t13;
    $[66] = t14;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  const body = t23;
  if (!service.image) {
    return body;
  }
  const t24 = service.image;
  const t25 = service.name;
  const t26 = usesSquareImage ? "aspect-square w-24 bg-background object-contain" : "aspect-video w-32 object-cover";
  let t27;
  if ($[69] !== t26) {
    t27 = cx("flex-shrink-0 rounded-lg", t26);
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== service.image || $[72] !== service.name || $[73] !== t27) {
    t28 = <img src={t24} alt={t25} className={t27} />;
    $[71] = service.image;
    $[72] = service.name;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== body || $[76] !== t28) {
    t29 = <div className="flex items-start gap-4">{t28}{body}</div>;
    $[75] = body;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  return t29;
};
interface ServiceMenuItemDesktopProps {
  descriptionDisplay?: "hover" | "inline";
  standalone?: boolean;
  service: PublicServiceDetail;
  isSelected: boolean;
  selectionCount?: number;
  maxQuantity?: number;
  disabled: boolean;
  onQuantityChange?: (serviceId: string, quantity: number) => void;
  showBookingFee: boolean;
  bookingFee: number;
  pricing: PricingInfo;
  onInfoClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hasAddons?: boolean;
  durationMin?: number;
  durationMax?: number;
  showDuration?: boolean;
  pricePrefix?: string;
}
export const ServiceMenuItemDesktop = props => {
  const $ = _c(14);
  const {
    isSelected,
    descriptionDisplay: t0
  } = props;
  const descriptionDisplay = t0 === undefined ? "hover" : t0;
  if (descriptionDisplay === "inline") {
    let t1;
    if ($[0] !== props) {
      t1 = <div className="hidden md:block"><Content {...props} expanded={true} collapsibleDescription={true} /></div>;
      $[0] = props;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== props) {
    t1 = <div className="hover:border-l-4 hover:border-primary/50"><Content {...props} /></div>;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isSelected ? "bg-gradient-to-bl from-primary/10 to-background via-background" : "bg-background";
  let t3;
  if ($[4] !== t2) {
    t3 = cx("absolute top-0 left-0 right-0 pl-3 pr-3 py-6", "opacity-0 group-hover:opacity-100", "transition-opacity duration-200", "rounded-lg shadow-lg", "border-l-4 border-primary/50", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== props) {
    t4 = <Content {...props} expanded={true} />;
    $[6] = props;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className="hidden md:block">{t1}{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
function _temp(event) {
  return event.stopPropagation();
}